import React from 'react';
import { Link } from '@remix-run/react';

/** A single internal-link entry in the mesh grid. */
export interface LinkGridItem {
    key: string;
    href: string;
    label: string;
}

const linkClassName =
    'line-clamp-6 cursor-pointer font-sans text-base leading-[1.3] text-gray-800 hover:underline';

/** Mesh blocks only link to same-origin directory paths. */
const isInternalHref = (href: string): boolean =>
    href.startsWith('/') && !href.startsWith('//');

interface CategoryPageLinkGridProps {
    heading: string;
    items: LinkGridItem[];
    /**
     * When true, the block sits directly under the FAQ divider so the FAQ
     * bottom border acts as this section's top line. Uses symmetric padding
     * above and below the heading/link content (gap-14.5, matching FAQ).
     */
    continuesFromPreviousSection?: boolean;
    /**
     * When true, omits the top border and top padding so this block stacks
     * beneath another mesh section that already ends with a divider.
     */
    stackedAfterMeshSection?: boolean;
    /**
     * When true, lays the links out as balanced CSS columns that flow
     * top-to-bottom then left-to-right, so an alphabetically-sorted list reads
     * down each column rather than across each row. The DOM order stays
     * sequential (good for screen readers); only the visual flow changes.
     * Defaults to the row-by-row grid layout.
     */
    flowDownColumns?: boolean;
}

/** Number of columns in the link grid on desktop. */
const COLUMN_COUNT = 4;

/**
 * Cap on rendered links. Curating the list keeps internal link equity
 * concentrated on the highest-value entries rather than diluting it across a long
 * tail; the full set remains reachable via the sitemap and parent hubs.
 *
 * Exported so callers that re-order links for display (e.g. alphabetical) can
 * select the same highest-value subset before sorting, keeping the rendered set
 * identical to what this grid would otherwise curate.
 */
export const LINK_GRID_MAX_ITEMS = 24;

/**
 * Shared presentational block for the location internal-link mesh: a heading over
 * a curated, server-rendered grid of anchors. Both the category-side location
 * block and the location-side category block render through this, so the mesh has
 * a single styling/curation home. Renders nothing when there are no items.
 */
const CategoryPageLinkGrid = ({
    heading,
    items,
    continuesFromPreviousSection = false,
    stackedAfterMeshSection = false,
    flowDownColumns = false,
}: CategoryPageLinkGridProps) => {
    if (items.length === 0) {
        return null;
    }

    const curated = items.slice(0, LINK_GRID_MAX_ITEMS);

    const meshContentClassName = '-mt-4 flex flex-col gap-10 pt-14.5 pb-14.5';
    const contentClassName =
        continuesFromPreviousSection || stackedAfterMeshSection
            ? meshContentClassName
            : 'flex flex-col gap-10 border-t border-gray-200 pt-14.5 pb-14.5';

    // Column flow mirrors COLUMN_COUNT on desktop; column-gap keeps the
    // horizontal spacing and per-item margin/break-inside keeps the vertical
    // rhythm while preventing a link from splitting across two columns.
    const listClassName = flowDownColumns
        ? 'columns-1 gap-x-5 md:columns-4'
        : `grid grid-cols-1 gap-5 md:grid-cols-${COLUMN_COUNT}`;
    const itemClassName = flowDownColumns
        ? 'mb-5 break-inside-avoid'
        : undefined;

    return (
        <div className="md:mb-spacing-page-category-gap-component-s px-6 md:px-12">
            <div className={contentClassName}>
                <h2 className="text-6 font-medium tracking-[0.025rem]">
                    {heading}
                </h2>
                <ul className={listClassName}>
                    {curated.map((item) => (
                        <li key={item.key} className={itemClassName}>
                            {isInternalHref(item.href) ? (
                                <Link
                                    to={item.href}
                                    prefetch="intent"
                                    className={linkClassName}
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <a href={item.href} className={linkClassName}>
                                    {item.label}
                                </a>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="border-t border-gray-200 pt-4" />
        </div>
    );
};

export default CategoryPageLinkGrid;
